import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Divider,
  Grid,
  Stack,
  Typography,
  useTheme,
  Link,
} from '@mui/material';
import { IPrescription, IOrder } from 'API';
import { PrescriptionStepper } from './PrescriptionStepper/index';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { PrescriptionStatusExtraInfo } from './PrescriptionStatusExtraInfo';
import { SupportIcon } from 'Images';
import { instanceOfIPrescription } from 'Utils';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';

interface PrescriptionStatusProps {
  item: IPrescription | IOrder;
}

export const PrescriptionStatus = (props: PrescriptionStatusProps) => {
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby
  );

  const theme = useTheme();
  const isDesktop = useIsDesktop();

  const { item: prescription } = props;

  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  const currentStep = useMemo(() => {
    return prescription.statuses.find(
      (status) => status.calcStatus === prescription.currentStatus
    );
  }, [prescription]);

  const getValidityText = (effectiveDate?: string) => {
    if (!effectiveDate)
      return isDesktop ? dictionary.ImmediateValidity : dictionary.Immediate;
    const differenceInTime =
      new Date(effectiveDate).getTime() - new Date().getTime();
    const differenceInDays = differenceInTime / (1000 * 3600 * 24);
    return differenceInDays >= 10
      ? isDesktop
        ? dictionary.prescriptionValidity(
            format(new Date(effectiveDate), 'dd/MM/yyyy')
          )
        : effectiveDate
      : isDesktop
      ? dictionary.ImmediateValidity
      : dictionary.Immediate;
  };

  const getCurrentStatusIndex = () =>
    prescription.statuses.findIndex(
      (status) => status.calcStatus === prescription.currentStatus
    );

  const type = prescription as unknown as IOrder;

  return (
    <Stack
      sx={{
        marginTop: 0,
        marginBottom: 3,
        [theme.breakpoints.up('sm')]: {
          marginTop: '40px',
          marginBottom: '56px',
        },
      }}
    >
      {isDesktop ? (
        <>
          <Typography
            sx={{
              marginBottom: '12px',
              [theme.breakpoints.up('sm')]: {
                marginBottom: '6px',
              },
            }}
          >
            {prescription.code !== 1
              ? `${dictionary.prescriptionCreatedDateInSystem(
                  format(new Date(prescription.createDate), 'dd/MM/yyyy')
                )} | ${getValidityText(
                  instanceOfIPrescription(prescription)
                    ? prescription.effectiveDate?.toString()
                    : ''
                )}`
              : `${dictionary.createdDate} ${format(
                  new Date(type.date),
                  'dd/MM/yyyy'
                )}`}
          </Typography>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: '18px',
              marginBottom: '16px',
              [theme.breakpoints.up('sm')]: {
                fontSize: '20px',
                marginBottom: '18px',
              },
            }}
            component='h3'
          >
            {dictionary.prescriptionStatus}
          </Typography>
          <Stack
            sx={{
              background: 'rgba(198, 211, 219, 0.08)',
              border: '1px solid rgba(198, 211, 219, 0.8)',
              borderRadius: '8px',
            }}
          >
            <Stack
              sx={{
                padding: '33px 16.5px 24px 16.5px',
                borderBottom: '1px solid rgba(198, 211, 219, 0.8)',
                [theme.breakpoints.up('sm')]: {
                  padding: '50px 34px 32px 34px',
                },
              }}
            >
              <PrescriptionStepper item={prescription} />
              {prescription.statuses[getCurrentStatusIndex()]?.description && (
                <PrescriptionStatusExtraInfo item={prescription} />
              )}
            </Stack>
            <Grid
              container
              justifyContent={'start'}
              sx={{
                paddingTop: '21px',
                paddingBottom: '16px',
                paddingLeft: '25px',
                paddingRight: '17px',
                [theme.breakpoints.up('sm')]: {
                  paddingLeft: '42px',
                },
              }}
              wrap={'nowrap'}
            >
              <Grid
                item
                sx={{
                  marginRight: '9px',
                  [theme.breakpoints.up('sm')]: {
                    marginRight: '12px',
                  },
                }}
              >
                <Avatar
                  alt=''
                  src={SupportIcon}
                  sx={{
                    width: 17,
                    height: 18,

                    [theme.breakpoints.up('sm')]: {
                      width: 21.25,
                      height: 21.25,
                    },
                  }}
                />
              </Grid>
              <Grid item>
                {currentStep && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '18px',
                        display: 'inline',
                        lineHeight: '25px',
                      }}
                    >{`${
                      !prescription.isManual && getCurrentStatusIndex() > 1
                        ? dictionary.superPharmSupport
                        : dictionary.contactUs1
                    }`}</Typography>
                    <Typography
                      component='a'
                      href={
                        isDesktop
                          ? undefined
                          : `tel: ${
                              !prescription.isManual &&
                              getCurrentStatusIndex() > 1
                                ? dictionary.superPharmPhone
                                : dictionary.manualSupplyPhoneTel
                            }`
                      }
                      sx={{
                        fontSize: '18px',
                        display: 'inline',
                        lineHeight: '25px',
                        fontWeight: 700,
                      }}
                    >{` ${
                      !prescription.isManual && getCurrentStatusIndex() > 1
                        ? dictionary.superPharmPhone
                        : dictionary.manualSupplyPhone
                    }`}</Typography>
                    {!prescription.isManual && getCurrentStatusIndex() > 1 && (
                      <>
                        <Divider
                          aria-hidden='true'
                          flexItem
                          orientation='vertical'
                          sx={{
                            height: '19px',
                            display: 'inline-flex',
                            margin: '0 -5px 0 10px',
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '18px',
                            display: 'inline',
                            lineHeight: '25px',
                            ml: 2,
                          }}
                        >
                          {dictionary.moreInfoSuperPharm}
                          <Link
                            sx={{ textDecoration: 'underline' }}
                            href={dictionary.moreInfoSuperPharmLinkUrl}
                            target='_blank'
                          >
                            {dictionary.moreInfoSuperPharmLinkTitle}
                          </Link>
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Stack>
        </>
      ) : (
        //Mobile version
        <>
          {prescription.code !== 1 ? (
            <>
              <Box>
                {`${dictionary.prescriptionCreatedDateInSystemMobile} `}
                <Box sx={{ fontWeight: '700', display: 'inline' }}>
                  {format(new Date(prescription.createDate), 'dd/MM/yyyy')}
                </Box>
              </Box>
              <Box>
                {`${dictionary.Validity}: `}
                <Box sx={{ fontWeight: '700', display: 'inline' }}>
                  {getValidityText(
                    instanceOfIPrescription(prescription)
                      ? prescription.effectiveDate?.toString()
                      : ''
                  )}
                </Box>
              </Box>
            </>
          ) : (
            <Box>
              {`${dictionary.createdDate}: `}
              <Box sx={{ fontWeight: '700', display: 'inline' }}>
                {format(new Date(type.date), 'dd/MM/yyyy')}
              </Box>
            </Box>
          )}
          <Accordion
            defaultExpanded
            sx={{
              width: '100%',
              boxShadow: '0px 4px 28px rgba(45, 112, 217, 0.15)',
              borderRadius: '16px !important',
              mt: '32px !important',
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              onClick={() => {
                setIsExpanded(!isExpanded);
              }}
              sx={{
                pt: 2.5,
                pb: '21px',
                pr: 3,
                pl: 3,
                boxShadow: isExpanded
                  ? '0px 4px 28px rgba(45, 112, 217, 0.15)'
                  : '',
                borderRadius: isExpanded ? '16px 16px 0 0' : '16px',
                '& .MuiAccordionSummary-content': {
                  margin: '0',
                },
              }}
            >
              <Grid container>
                <Typography>{dictionary.prescriptionStatus}</Typography>
                <Divider
                  aria-hidden='true'
                  sx={{
                    width: '85%',
                    mt: '10px',
                    mb: 2,
                  }}
                />
                <Typography fontWeight={700}>
                  {currentStep?.label || ''}
                </Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Grid>
                <PrescriptionStepper item={prescription} />
                <PrescriptionStatusExtraInfo item={prescription} />
              </Grid>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Stack>
  );
};
